import { toDisplayOddsDecimal } from '../../pages/Sports/_helpers/oddsFormat.js';

const REMOVABLE_STATUSES = new Set(['locked', 'unavailable']);

const normalizedStatus = selection => String(
  selection?.status || 'active',
).trim().toLowerCase();

const acceptedPendingOdds = selection => (
  normalizedStatus(selection) === 'odds_changed'
    ? toDisplayOddsDecimal(selection?.pendingOdds)
    : null
);

export const summarizeSportsBetCartChanges = (selections = []) => {
  const summary = {
    changedCount: 0,
    removedCount: 0,
    unresolvedCount: 0,
    actionableCount: 0,
  };

  (Array.isArray(selections) ? selections : []).forEach((selection) => {
    const status = normalizedStatus(selection);
    if (REMOVABLE_STATUSES.has(status)) {
      summary.removedCount += 1;
      return;
    }
    if (status === 'odds_changed' && acceptedPendingOdds(selection) != null) {
      summary.changedCount += 1;
      return;
    }
    if (status !== 'active') summary.unresolvedCount += 1;
  });

  summary.actionableCount = summary.changedCount + summary.removedCount;
  return summary;
};

export const resolveSportsBetCartBulkAcceptance = (selections = []) => {
  const source = Array.isArray(selections) ? selections : [];
  const summary = summarizeSportsBetCartChanges(source);
  const acceptedSelections = [];

  source.forEach((selection) => {
    const status = normalizedStatus(selection);
    if (REMOVABLE_STATUSES.has(status)) return;

    const nextOdds = acceptedPendingOdds(selection);
    if (status === 'odds_changed' && nextOdds != null) {
      acceptedSelections.push({
        ...selection,
        odds: nextOdds,
        pendingOdds: undefined,
        status: 'active',
      });
      return;
    }

    acceptedSelections.push(selection);
  });

  return { selections: acceptedSelections, summary };
};

const validBulkAcceptanceCommand = command => (
  command
  && typeof command.requestId === 'string'
  && command.requestId.length > 0
  && command.requestId.length <= 160
  && typeof command.ownerTag === 'string'
  && command.ownerTag.length > 0
  && command.ownerTag.length <= 160
  && Number.isSafeInteger(command.expectedRevision)
  && command.expectedRevision >= 0
);

/**
 * Pure functional-update boundary for cart-wide requote acceptance.
 * A stale account owner or revision returns the exact prior state reference.
 */
export const reduceSportsBetCartBulkAcceptance = ({ state, command } = {}) => {
  if (
    !state
    || !Array.isArray(state.selections)
    || typeof state.ownerTag !== 'string'
    || !Number.isSafeInteger(state.revision)
    || !validBulkAcceptanceCommand(command)
    || command.ownerTag !== state.ownerTag
    || command.expectedRevision !== state.revision
  ) return state;

  const resolved = resolveSportsBetCartBulkAcceptance(state.selections);
  const committedRevision = state.revision + 1;
  const summary = Object.freeze({ ...resolved.summary });
  const applyReceipt = Object.freeze({
    request_id: command.requestId,
    owner_tag: command.ownerTag,
    expected_revision: command.expectedRevision,
    committed_revision: committedRevision,
    applied: true,
    summary,
  });

  return {
    ...state,
    selections: resolved.selections,
    stake: resolved.selections.length === 0 ? '' : state.stake,
    revision: committedRevision,
    applyReceipt,
  };
};

export default Object.freeze({
  summarizeSportsBetCartChanges,
  resolveSportsBetCartBulkAcceptance,
  reduceSportsBetCartBulkAcceptance,
});
